import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { PLANS, whatsappLink } from "@/data/content";

export default function Pricing() {
  return (
    <section id="piani" data-testid="pricing-section" className="relative py-24 lg:py-32">
      <div className="absolute inset-x-0 top-1/4 h-72 teal-glow pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">I piani</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Due piani chiari. Tutto già compreso.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Strategia, idee, script, riprese, montaggio e sponsorizzazioni sono già comprese nel
            prezzo mensile. Nessun costo aggiuntivo e nessuna sorpresa.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-3xl p-8 lg:p-10 border ${
                p.recommended
                  ? "border-teal-light/50 bg-[#0c1413] shadow-[0_8px_60px_rgba(45,212,191,0.12)] lg:-translate-y-3"
                  : "border-white/10 bg-[#0b0b0b]"
              }`}
            >
              <div className="absolute -top-3 left-8 flex flex-wrap gap-2">
                {p.recommended && (
                  <span data-testid={`badge-piuscelto-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal-light px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#050505]">
                    <Star size={13} /> Più scelto
                  </span>
                )}
                <span data-testid={`badge-sconto-${p.id}`} className="inline-flex items-center rounded-full bg-red-500 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
                  100 € di sconto
                </span>
              </div>

              <h3 className="mt-3 font-display text-2xl text-white">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-400">{p.videos}</p>

              <div className="mt-6 flex items-end gap-3">
                <span className="font-display text-5xl lg:text-6xl text-white">{p.price} €</span>
                <span className="mb-2 text-slate-400">/ mese</span>
                <span className="mb-2 text-lg text-slate-500 line-through decoration-red-500/70">{p.oldPrice} €</span>
              </div>

              <p className="mt-4 text-sm text-slate-300 leading-relaxed">{p.tagline}</p>

              <div className="mt-4 rounded-xl border border-teal-light/20 bg-teal/10 px-4 py-3 text-sm text-teal-light">
                <strong>{p.adBudget}</strong> del prezzo mensile vengono già destinati alle sponsorizzazioni.
              </div>

              <p className="mt-3 text-sm font-medium text-white">
                Hai già 100 € di sconto: da {p.oldPrice} € a {p.price} € al mese.
              </p>

              <ul className="mt-6 space-y-3">
                {p.features.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-3 text-sm text-slate-200">
                    <Check size={17} className="mt-0.5 shrink-0 text-teal-light" />
                    {f}
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                Prezzo completo e definitivo, comprensivo di tasse, produzione, strategia e budget
                pubblicitario previsto. Nessun costo nascosto.
              </p>

              <a
                data-testid={`plan-cta-${p.id}`}
                href={whatsappLink(p.waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-6 block text-center w-full rounded-full py-3.5 text-sm font-semibold transition-colors ${
                  p.recommended
                    ? "bg-teal-light text-[#050505] hover:bg-white"
                    : "bg-white/8 text-white border border-white/15 hover:bg-white/15"
                }`}
              >
                Scegli {p.name}
              </a>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 mx-auto max-w-3xl rounded-2xl border border-teal-light/20 bg-[#0b0b0b] px-6 py-7 text-center">
          <h3 className="font-display text-xl text-white">Quello che vedi è quello che paghi</h3>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            I prezzi indicati comprendono il servizio previsto, la produzione dei contenuti, la
            strategia, le tasse e il budget pubblicitario incluso. Non verranno aggiunti costi extra
            o spese impreviste.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row justify-center gap-3">
            <span className="rounded-full border border-white/10 bg-[#101010] px-4 py-2 text-sm text-teal-light">Fast 8: 49,90 € già destinati alle sponsorizzazioni</span>
            <span className="rounded-full border border-white/10 bg-[#101010] px-4 py-2 text-sm text-teal-light">Fast 12: 69,90 € già destinati alle sponsorizzazioni</span>
          </div>
        </div>
      </div>
    </section>
  );
}
